import './Login.less';

import { useMount } from 'ahooks';
import { Alert } from 'antd';
import { useAppDispatch } from 'app/hooks';
import birdLogo from 'assets/img/bird.svg';
import logo from 'assets/img/logo.svg';
import albertsonsLogo from 'assets/img/Albertsons-Companies-Logo.png';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  CreatePassword,
  ForgotPassword,
  LoginFormComponent,
} from './components';

function formatCanvaSsoError(code: string): string {
  switch (code) {
    case 'invalid_or_expired_handoff':
      return 'This Canva sign-in link expired or was already used. Open RevShop again from Canva.';
    case 'user_inactive':
      return 'Your account is inactive. Contact support.';
    case 'exchange_failed':
      return 'Could not finish signing in from Canva. Try again from the Canva app or use email login.';
    default:
      return 'Could not complete sign-in from Canva. Try again from the Canva app or use email login.';
  }
}

const Login = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { search, pathname } = useLocation();

  const [step, setStep] = useState<'login' | 'create' | 'forgot'>('login');
  const [canvaSsoMessage, setCanvaSsoMessage] = useState<string>();

  useEffect(() => {
    const searchObj = new URLSearchParams(search);
    const canvaErr = searchObj.get('canva_sso_error');
    if (canvaErr) {
      setCanvaSsoMessage(formatCanvaSsoError(canvaErr));
      searchObj.delete('canva_sso_error');
      const qs = searchObj.toString();
      window.history.replaceState(
        {},
        '',
        `${pathname}${qs ? `?${qs}` : ''}`
      );
    }
    const token = searchObj.get('token');
    const tokenType = searchObj.get('type');
    const redirectTo = searchObj.get('redirect');

    if (token && tokenType) {
      dispatch.account.setToken({ token, tokenType });
      if (tokenType === 'VERIFY') {
        setStep('login');
      } else {
        setStep('create');
      }
    } else if (redirectTo) {
      dispatch.account.setRedirect(redirectTo);
      setStep('login');
    }
  }, [search, pathname, dispatch.account]);

  useMount(() => {
    dispatch.app.logout();
  });

  const actionComponent = {
    login: LoginFormComponent,
    create: CreatePassword,
    forgot: ForgotPassword,
  };

  const isAlbertsonsSite = window.location.hostname.endsWith(
    'albertsonsprintcenter.com'
  );

  return (
    <div className="login">
      {canvaSsoMessage && (
        <Alert
          type="error"
          showIcon
          closable
          message={canvaSsoMessage}
          onClose={() => setCanvaSsoMessage(undefined)}
          style={{ maxWidth: 520, margin: '0 auto 16px' }}
        />
      )}
      {isAlbertsonsSite ? (
        <div className="login-text albertsons-content">
          <h1>Welcome to the Albertsons Print Center</h1>
          <h2>Your streamlined solution for professional print services!</h2>

          <div className="features-list">
            <p>
              <strong>Order with ease</strong> from a standardized product
              gallery, enabling you to customize your print job with just a few
              clicks.
            </p>

            <p>
              <strong>Automated routing</strong> sends your request to the
              optimal Albertsons printing resource.
            </p>

            <p>
              <strong>Track your order</strong> status anytime through your
              personalized Dashboard.
            </p>

            <p>
              <strong>Seamless billing</strong> is handled automatically through
              cost center charging—no manual or external invoicing needed.
            </p>
          </div>

          <img
            className="albertsons-logo"
            src={albertsonsLogo}
            alt="Albertsons Print Center"
          />
        </div>
      ) : (
        <div className="login-text">
          <h1>
            The print <span className="green flip">rev</span>olution is{' '}
            <span className="cmyk-text">
              <span className="cyan">h</span>
              <span className="magenta">e</span>
              <span className="yellow">r</span>
              <span className="black">e</span>
              <img className="flower-icon" src={logo} alt="revshop" />
            </span>
          </h1>
          <p>
            {t('login.pageDescription')}
            <span className="green">{t('login.pageWelcome')}</span>
          </p>
          <img className="big-logo-icon" src={logo} alt="revshop" />
          <img className="bird-icon" src={birdLogo} alt="revshop" />
        </div>
      )}
      {React.createElement(actionComponent[step], { setStep })}
    </div>
  );
};

export default Login;
